/**
 * Centralized due date calculation utilities
 *
 * All due date calculations should use these functions to ensure consistency
 * across the application (timezone handling, end-of-day normalization, etc.)
 */

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

/** Default days until due if not specified */
export const DEFAULT_DUE_DATE_DAYS = 21;

/**
 * Calculate due date from a base timestamp + days
 * Used when we need to calculate a due date (e.g., for Stripe bills sent)
 *
 * @param baseTimestamp - Unix timestamp (seconds) to calculate from
 * @param days - Number of days to add
 * @param timeZone - Property timezone (defaults to browser timezone)
 * @returns Unix timestamp (milliseconds) at end of due date day
 */
export const calculateDueDateFromTimestamp = (
  baseTimestamp: number,
  days: number,
  timeZone?: string | null,
): number => {
  const effectiveTimeZone = timeZone || dayjs.tz.guess();
  const baseDate = dayjs.unix(baseTimestamp).tz(effectiveTimeZone);
  const dueDate = baseDate.add(days, 'day');

  return dueDate
    .set('hours', 23)
    .set('minutes', 59)
    .set('seconds', 59)
    .set('milliseconds', 0)
    .valueOf();
};

/**
 * Calculate due date from today + days
 * Used for preview/estimate before invoice is generated
 *
 * @param days - Number of days to add to today
 * @param timeZone - Property timezone (defaults to browser timezone)
 * @returns Unix timestamp (milliseconds) at end of due date day
 */
export const calculateDueDateFromToday = (days: number, timeZone?: string | null): number => {
  const effectiveTimeZone = timeZone || dayjs.tz.guess();
  const todayInPropertyTz = dayjs().tz(effectiveTimeZone);
  const dueDate = todayInPropertyTz.add(days, 'day');

  return dueDate
    .set('hours', 23)
    .set('minutes', 59)
    .set('seconds', 59)
    .set('milliseconds', 0)
    .valueOf();
};

/**
 * Parse an ISO date string (YYYY-MM-DD) into a timestamp
 * Used when we have an actual stored due date from the database
 *
 * @param isoDateString - ISO date string (e.g., "2026-02-24")
 * @param timeZone - Property timezone to interpret the date in
 * @returns Unix timestamp (milliseconds) at end of that day
 */
export const parseDueDateString = (isoDateString: string, timeZone?: string | null): number => {
  const effectiveTimeZone = timeZone || dayjs.tz.guess();
  return dayjs.tz(isoDateString, effectiveTimeZone).endOf('day').valueOf();
};

/**
 * Get the due date for display, using actual stored date if available
 * This is the main function to use when displaying due dates
 *
 * @param customInvoiceDueDate - Actual due date from custom invoice (ISO string or null)
 * @param fallbackTimestamp - Timestamp to calculate from if no actual date (unix seconds)
 * @param dueDateDays - Days to add for calculation
 * @param timeZone - Property timezone
 * @returns Unix timestamp (milliseconds) or null if no due date exists
 */
export const getDueDate = (
  customInvoiceDueDate: string | null | undefined,
  fallbackTimestamp: number | null | undefined,
  dueDateDays: number,
  timeZone?: string | null,
): number | null => {
  // If we have an actual stored due date, use it
  if (customInvoiceDueDate) {
    return parseDueDateString(customInvoiceDueDate, timeZone);
  }

  // If we have a fallback timestamp (e.g., billsSentTimestamp), calculate from it
  if (fallbackTimestamp) {
    return calculateDueDateFromTimestamp(fallbackTimestamp, dueDateDays, timeZone);
  }

  // No due date exists yet
  return null;
};

/**
 * Format a due date timestamp for display
 *
 * @param dueDateTimestamp - Unix timestamp (milliseconds)
 * @param timeZone - Property timezone
 * @returns Formatted date string (MM/DD/YYYY)
 */
export const formatDueDate = (
  dueDateTimestamp: number | null,
  timeZone?: string | null,
): string => {
  if (!dueDateTimestamp) {
    return '—';
  }
  const effectiveTimeZone = timeZone || dayjs.tz.guess();
  return dayjs(dueDateTimestamp).tz(effectiveTimeZone).format('MM/DD/YYYY');
};
